import { 
	IDictionary,
	ClientFeature,
    FeatureTogglePoint,
    IStorageAccessor,
 } from 'util/domain';
 import { CommonUtilsService } from 'util/services';

type AsyncOrSync<T> = T | ng.IPromise<T>;
export interface IToggleService {
    add(feature:ClientFeature):IToggleService;
    getFeature(featureName:string):AsyncOrSync<ClientFeature>;
    if<T>(featureName:string, object:T):AsyncOrSync<T>;
    isEnabled(featureName:string):AsyncOrSync<boolean>;
    isEnabled(...features:string[]):AsyncOrSync<IDictionary<boolean>>;
    not<T>(featureName:string, object:T):AsyncOrSync<T>;
    set(featureName:string, enabled:boolean):IToggleService|ng.IPromise<void>;
    with(featureName:string):FeatureTogglePoint;
}

/**
 * General-purpose toggle service used for setting local,
 * static toggles. This is intended to be agnostic of 
 * any business logic or application-specific concepts.
 */
export class StaticToggleService implements IToggleService {
    static $inject = [
    ];

    public readonly features:IDictionary<ClientFeature> = {};
    constructor(
		preloadedFeatures:ClientFeature[] = []
    ){
		preloadedFeatures.forEach(f => this.add(f));
    }
    
    public add(feature:ClientFeature) {
        if(!(feature.name in this.features)){
            this.features[feature.name] = feature;
        }
        return this;
    }

    public remove(featureName:string){
        if(featureName in this.features){
            delete this.features[featureName];
        }
    }

    public getFeature(featureName:string) {
        return this.features[featureName];
    }

    public isEnabled(featureName:string):boolean;
    public isEnabled(...features:string[]):IDictionary<boolean>;
    public isEnabled(...features:string[]) {
        const isEnabled = (fName:string) => fName in this.features && this.getFeature(fName).enabled;
        if(features.length === 1){
            return isEnabled(features[0]);
        }
        else {
            return features.reduce((dict, f) => {
                dict[f] = isEnabled(f);
                return dict;
            }, {} as IDictionary<boolean>);
        }        
    }

    public set(featureName:string, enabled:boolean) {
        if(featureName in this.features){
            this.getFeature(featureName).enabled = enabled;
        }
        else {
            this.add({
                name: featureName,
                enabled
            });
        }

        return this;
    }

    public with(featureName:string) {
       let togglePoint:FeatureTogglePoint = {
            on: callback => {
                if(this.isEnabled(featureName)){
                    callback();
                }

                return togglePoint;
            },
            off: callback => {
                if(!this.isEnabled(featureName)){
                    callback();
                }

                return togglePoint;
            },
            either: callback => {
                callback();
                return togglePoint;
            }
       }

       return togglePoint;
    }

    /**
     * Returns the provided object if the feature in question is turned on.
     * @param featureName 
     * @param object 
     */
    public if<T>(featureName:string, object:T) {
        if(this.isEnabled(featureName)){
            return object;
        }
        else {
            return null;
        }
    }

    /**
     * Returns the provided object if the feature in question is turned off.
     * @param featureName 
     * @param object 
     */
    public not<T>(featureName:string, object:T) {
        if(!this.isEnabled(featureName)){
            return object;
        }
        else {
            return null;
        }
    }
}

export class StaticToggleServiceProvider implements ng.IServiceProvider {
    private preloadedFeatures:ClientFeature[] = [];
    public readonly $get = [
        () =>  new StaticToggleService(this.preloadedFeatures)
    ];

    constructor() {
    }

    public addFeature(feature:ClientFeature){
        this.preloadedFeatures.push(feature);
        return this;
    }
};

interface StorageToggles {
    toggles:IDictionary<boolean>;
    fingerprint:string;
}

export class StorageToggleService extends StaticToggleService {
    static $inject = [
        'utils',
        'sessionStorageService'
    ];

    private storageToggles:IDictionary<boolean> = this.getTogglesFromStorage();
    constructor(
        private util:CommonUtilsService,
        private storage:IStorageAccessor,
        private storageKey:string,
        preloadedFeatures?:ClientFeature[]

    ){
        super(preloadedFeatures);  
        angular.forEach(this.storageToggles, (enabled, name) => {
            super.set(name, enabled);
        });
    }

    private getTogglesFromStorage() {
        let serialized = this.storage.getStoredValue(this.storageKey);
        let features:IDictionary<boolean> =  {};

        if(serialized){
            try{                
                let payload:StorageToggles = JSON.parse(serialized);
                const fingerprint = this.util.generateHash(JSON.stringify(payload.toggles));
                
                if(payload.fingerprint !== fingerprint){
                    this.storage.removeStoredValue(this.storageKey);
                    throw new Error('Incorrect checksum');
                }

                features = payload.toggles;
            }
            catch(e){
                console.error(e);
            }
        }

        return features;
    }

    public set(featName:string, enabled:boolean, persist:boolean = false){
        if(persist){ 
            this.storageToggles[featName] = enabled;
                        
            let payload:StorageToggles = {
                toggles:this.storageToggles,
                fingerprint: this.util.generateHash(JSON.stringify(this.storageToggles))
            };
            
            this.storage.setStoredValue(this.storageKey, JSON.stringify(payload));
        }

        return super.set(featName, enabled);
    }
}

export class StorageToggleServiceProvider implements ng.IServiceProvider {
    private storageKey:string;
    private preloadedFeatures:ClientFeature[] = [];
    public readonly $get = [
        'utils',
        'sessionStorageService',(
            utils:CommonUtilsService,
            storage:IStorageAccessor
        ) =>  {
            return new StorageToggleService(utils, storage, this.storageKey)
        }
    ];

    constructor() {
    }

    public setKey(storageKey:string){
        this.storageKey = storageKey;
        return this;
    }

    public addFeature(feature:ClientFeature){
        this.preloadedFeatures.push(feature);
        return this;
    }
};


// WEBPACK FOOTER //
// ./src/util/services/staticToggleService.ts